import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import React, { useState, useEffect } from 'react';
import { getPasswordStrength } from 'helper/generalFunction';
import useRedux from 'redux/useRedux';
import { AuthRegisterMerchant, UniqueUsernameNMailCheck, GetBillingSignature } from 'redux/auth/auth.services';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CircularProgress } from '@mui/material';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";

function Register() {
  const { thunkDispatch } = useRedux();
  const navigate = useNavigate();
  const [step, setStep] = useState(() => {
    const saved = sessionStorage.getItem('registration_step');
    const parsed = saved ? parseInt(saved, 10) : 1;
    return parsed >= 4 ? 1 : parsed;
  });
  const [metaOption, setMetaOption] = useState('team'); // 'auto' or 'team'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiResult, setApiResult] = useState<any>(null);
  const [errors, setErrors] = useState<any>({});
  const [paymentUrl, setPaymentUrl] = useState('');
  const [signatureToken, setSignatureToken] = useState('');
  const [isSignatureLoading, setIsSignatureLoading] = useState(false);
  const [signatureError, setSignatureError] = useState('');
  const [showMonthlyPayment, setShowMonthlyPayment] = useState(false);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [isPaymentSuccess, setIsPaymentSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showWaTooltip, setShowWaTooltip] = useState(false);
  const [hadWhatsappNumber, setHadWhatsappNumber] = useState(false);

  const platformFeePercentage = process.env.REACT_APP_PLATFORM_FEE_PERCENTAGE || '5';
  const monthlyFeeAmount = process.env.REACT_APP_MONTHLY_FEE_AMOUNT || '850000';

  // Format monthly fee to display (e.g. 850000 -> 850k or formatted string)
  const formattedMonthlyFee = (parseInt(monthlyFeeAmount) / 1000) + 'k';

  const [formData, setFormData] = useState(() => {
    const saved = sessionStorage.getItem('registration_formData');
    return saved ? JSON.parse(saved) : {
      business_name: '',
      owner_name: '',
      username: '',
      email: '',
      phone_number: '',
      whatsapp_number: '',
      location: '',
      password: '',
      confirm_password: '',
      agree_terms: false,

      // Meta Data
      meta_business_id: '',
      meta_app_id: '', // import.meta.env.VITE_FB_APP_ID || '',
      meta_system_user_token: '',
      whatsapp_phone_number_id: '',
      payment_method: 'Bank Transfer (Virtual Account)',
      payment_type: 'MONTHLY_FEE',
    };
  });

  // Save to sessionStorage
  useEffect(() => {
    if (step < 5) {
      sessionStorage.setItem('registration_formData', JSON.stringify(formData));
    } else {
      sessionStorage.removeItem('registration_formData');
    }
  }, [formData, step]);

  useEffect(() => {
    if (step < 5) {
      sessionStorage.setItem('registration_step', step.toString());
    } else {
      sessionStorage.removeItem('registration_step');
    }
  }, [step]);

  // Load Facebook SDK
  useEffect(() => {
    (window as any).fbAsyncInit = function () {
      (window as any).FB.init({
        appId: '', // import.meta.env.VITE_FB_APP_ID,
        cookie: true,
        xfbml: true,
        version: 'v19.0'
      });
    };
    (function (d: any, s: any, id: any) {
      var js: any, fjs: any = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) return;
      js = d.createElement(s); js.id = id;
      js.src = "https://connect.facebook.net/en_US/sdk.js";
      fjs.parentNode.insertBefore(js, fjs);
    }(document, 'script', 'facebook-jssdk'));
  }, []);

  // Listen to Meta's message
  useEffect(() => {
    const handler = (event: any) => {
      if (event.origin !== "https://www.facebook.com" && event.origin !== "https://web.facebook.com") return;
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'WA_EMBEDDED_SIGNUP' && data.event === 'FINISH') {
          const { phone_number_id, waba_id } = data.data;
          setFormData((prev: any) => ({ ...prev, whatsapp_phone_number_id: phone_number_id, meta_business_id: waba_id }));
          alert("WhatsApp Berhasil Terhubung!");
        }
      } catch (e) { }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  const handleChange = (e: any) => {
    const { name, value, type, checked } = e.target;
    let finalValue = type === 'checkbox' ? checked : value;
    if (name === 'phone_number' || name === 'whatsapp_number') {
      finalValue = value.replace(/[^0-9]/g, '');
    }
    setFormData((prev: any) => ({ ...prev, [name]: finalValue }));
    if (errors[name]) setErrors((prev: any) => ({ ...prev, [name]: '' }));
    if (signatureToken) {
      setSignatureToken('');
      setSignatureError('');
    }
    if (name === 'payment_type') {
      setShowMonthlyPayment(false);
    }
  };

  const passwordStrength = getPasswordStrength(formData.password);
  const hasUppercase = /[A-Z]/.test(formData.password);
  const hasNumber = /[0-9]/.test(formData.password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(formData.password);

  const getStepErrors = (currentStep: any) => {
    let newErrors: any = {};
    if (currentStep === 1) {
      if (!formData.business_name.trim()) newErrors.business_name = 'Nama Bisnis wajib diisi';
      if (!formData.owner_name.trim()) newErrors.owner_name = 'Nama Pemilik wajib diisi';
      if (!formData.username.trim()) newErrors.username = 'Username wajib diisi';
      else if (/\s/.test(formData.username)) newErrors.username = 'Username tidak boleh mengandung spasi';
      if (!formData.email.trim()) {
        newErrors.email = 'Email wajib diisi';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) { newErrors.email = 'Email tidak valid'; }
      if (!formData.phone_number.trim()) newErrors.phone_number = 'Nomor Telepon wajib diisi';
      else if (formData.phone_number.length < 8 || formData.phone_number.length > 15) newErrors.phone_number = 'Panjang nomor telepon tidak valid';

      if (formData.whatsapp_number && formData.whatsapp_number.trim() !== '') {
        if (formData.whatsapp_number.length < 8 || formData.whatsapp_number.length > 15) newErrors.whatsapp_number = 'Panjang nomor WhatsApp AI tidak valid';
      }
      if (!formData.password.trim()) { newErrors.password = 'Kata sandi wajib diisi'; }
      else if (formData.password.length < 8) { newErrors.password = 'Kata sandi minimal 8 karakter'; }
      else if (!hasUppercase) { newErrors.password = 'Kata sandi harus memiliki huruf besar'; }
      else if (!hasNumber) { newErrors.password = 'Kata sandi harus memiliki angka'; }
      else if (!hasSpecialChar) { newErrors.password = 'Kata sandi harus memiliki karakter spesial'; }
      else if (passwordStrength.score < 2) { newErrors.password = 'Kata sandi terlalu lemah'; }
      if (!formData.confirm_password.trim()) { newErrors.confirm_password = 'Konfirmasi kata sandi wajib diisi'; }
      else if (formData.password !== formData.confirm_password) { newErrors.confirm_password = 'Konfirmasi sandi tidak cocok'; }
      if (!formData.agree_terms) newErrors.agree_terms = 'Anda harus menyetujui Ketentuan Layanan';
    }
    if (currentStep === 2 && metaOption === 'auto') {
      if (!formData.whatsapp_phone_number_id) newErrors.global = 'Silakan hubungkan WhatsApp Anda terlebih dahulu';
    }
    return newErrors;
  };

  const validateStep = (currentStep: any) => {
    const newErrors = getStepErrors(currentStep);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    // 1. Get local errors
    let combinedErrors = getStepErrors(step);

    // 2. Perform unique check if in Step 1 and username/email are locally valid
    if (step === 1 && !combinedErrors.username && !combinedErrors.email) {
      setIsSubmitting(true);
      try {
        const resultAction: any = await thunkDispatch(UniqueUsernameNMailCheck({
          username: formData.username,
          email: formData.email
        }));
        const response = resultAction.payload;

        if (response && response.status === 'success') {
          const { usernameAvailable, emailAvailable } = response.data;
          if (!usernameAvailable) combinedErrors.username = 'Username sudah terdaftar';
          if (!emailAvailable) combinedErrors.email = 'Email sudah terdaftar';
        } else {
          combinedErrors.global = 'Gagal melakukan validasi data. Silakan coba lagi.';
        }
      } catch (error: any) {
        combinedErrors.global = 'Terjadi kesalahan saat validasi.';
      } finally {
        setIsSubmitting(false);
      }
    }

    // 3. Update errors state once with all collected errors
    setErrors(combinedErrors);

    // 4. Proceed only if no errors found
    if (Object.keys(combinedErrors).length === 0) {
      if (step === 3) {
        if (formData.payment_type === 'PLATFORM_FEE') {
          handleRegisterMerchant();
        } else {
          setShowMonthlyPayment(true);
          window.scrollTo(0, 0);
        }
      } else {
        setStep(step + 1);
        window.scrollTo(0, 0);
      }
    }
  };

  const handleRegisterMerchant = React.useCallback(async () => {
    setIsPaymentLoading(true);
    try {
      const payload = {
        username: formData.username,
        name: formData.owner_name,
        email: formData.email,
        password: formData.password,
        businessName: formData.business_name,
        businessOwnerName: formData.owner_name,
        businessNumber: '62' + formData.phone_number,
        whatsappNumber: formData.whatsapp_number ? '62' + formData.whatsapp_number : undefined,
        meta_catalog_id: '',
        payment_type: formData.payment_type,
        ...(formData.payment_type === 'PLATFORM_FEE' ? {
          service_fee: 5,
          service_fee_type: 'MERCHANT',
          tax_type: 'MERCHANT'
        } : {
          service_fee: null,
          service_fee_type: null,
          tax_type: null
        })
      };

      const resultAction: any = await thunkDispatch(AuthRegisterMerchant(payload));
      const response = resultAction.payload;

      if (response && response.status === 'success') {
        toast.success("Akun berhasil didaftarkan");
        setHadWhatsappNumber(!!(formData.whatsapp_number && formData.whatsapp_number.trim() !== ''));
        setStep(4);
        localStorage.clear();
        sessionStorage.clear();
      } else {
        throw new Error(response?.data || 'Terjadi kesalahan saat pendaftaran');
      }
    } catch (error: any) {
      toast.error(error.message);
      setErrors((prev: any) => ({ ...prev, global: error.message }));
    } finally {
      setIsPaymentLoading(false);
    }
  }, [formData, thunkDispatch]);

  useEffect(() => {
    const handleWidgetMessage = async (e: MessageEvent) => {
      if (e.data.event === 'pagii_payment_success' || e.data.event === 'pagii_trial_success') {
        console.log('Success Received!', e.data.event, e.data.data);
        setIsPaymentSuccess(true);
        handleRegisterMerchant();
      }

      // Basic logging for other messages from the billing domain
      if (e.origin.includes('pagii-billing.stg8.smtapps.net') || e.origin.includes('localhost:8000') || e.origin.includes('127.0.0.1:8000')) {
        console.log('Widget Billing Message:', e.data);
      }
    };

    window.addEventListener('message', handleWidgetMessage);

    return () => {
      window.removeEventListener('message', handleWidgetMessage);
    };
  }, [handleRegisterMerchant]);

  const fetchBillingSignature = React.useCallback(async () => {
    setIsSignatureLoading(true);
    setSignatureError('');
    try {
      let formattedPhone = formData.phone_number || '';
      if (formattedPhone.startsWith('62')) {
        formattedPhone = '0' + formattedPhone.slice(2);
      } else if (formattedPhone && !formattedPhone.startsWith('0')) {
        formattedPhone = '0' + formattedPhone;
      }

      const resultAction: any = await thunkDispatch(
        GetBillingSignature({
          name: formData.owner_name,
          email: formData.email,
          phone: formattedPhone,
          company: formData.business_name,
          address: formData.location,
        })
      );
      const res = resultAction.payload;

      if (res && res.status === 'success' && res.data?.signature) {
        setSignatureToken(res.data.signature);
      } else {
        const errorMsg = res?.error || res?.data?.message || 'Gagal memuat formulir pembayaran aman. Silakan coba lagi.';
        throw new Error(errorMsg);
      }
    } catch (err: any) {
      setSignatureError(err.message || 'Terjadi kesalahan saat menyiapkan formulir pembayaran.');
    } finally {
      setIsSignatureLoading(false);
    }
  }, [formData, thunkDispatch]);

  useEffect(() => {
    if (step === 3 && formData.payment_type === 'MONTHLY_FEE' && showMonthlyPayment) {
      if (!signatureToken && !isSignatureLoading && !signatureError) {
        fetchBillingSignature();
      }
    }
  }, [step, formData.payment_type, showMonthlyPayment, signatureToken, isSignatureLoading, signatureError, fetchBillingSignature]);

  return (
    <div className="font-['Inter',-apple-system,sans-serif] bg-slate-50 min-h-screen flex items-center justify-center p-4 sm:p-8 text-slate-900">
      <div className="bg-white w-full max-w-[750px] rounded-xl shadow-[0_10px_25px_-5px_rgba(0,0,0,0.05)] overflow-hidden relative">
        <div className="text-center py-10 px-8 pb-6">
          <img src="https://pagii.co/wp-content/uploads/2023/09/ProductChatshop-VariantDefault-BackgroundLight-1.svg" alt="Chatshop" className="h-[42px] mx-auto mb-5 block" />
          <h1 className="m-0 text-2xl font-bold text-slate-900">Pendaftaran Merchant</h1>
          <p className="mt-2 text-slate-500 text-[0.95rem]">Lengkapi informasi untuk memulai layanan WhatsApp Commerce.</p>
        </div>

        <div className="flex justify-between items-center mb-8 px-4 sm:px-12">
          {[1, 2, 3, 4].map((s: any) => (
            <React.Fragment key={s}>
              <div className="flex flex-col items-center relative z-10 w-[60px]">
                <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center font-semibold text-sm transition-all duration-300 ${step > s || (step === 4 && s === 4)
                  ? 'bg-emerald-500 border-emerald-500 text-white'
                  : step === s
                    ? 'bg-white border-emerald-500 text-emerald-500 ring-4 ring-emerald-500/20'
                    : 'bg-white border-slate-200 text-slate-400'
                  }`}>
                  {s}
                </div>
              </div>
              {s < 4 && <div className={`flex-1 h-[0.5px] relative z-0 ${step > s ? 'bg-emerald-500' : 'bg-slate-200'}`}></div>}
            </React.Fragment>
          ))}
        </div>

        <div className="px-6 sm:px-12 pb-8">
          {errors.global && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg flex items-center gap-3 mb-6 text-sm text-left">
              <svg fill="none" viewBox="0 0 24 24" className="w-5 h-5 flex-shrink-0 stroke-red-700">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <strong>Gagal Terhubung:</strong> {errors.global === 'Failed to fetch' ? 'Server backend tidak merespon atau belum dijalankan.' : errors.global}
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-0">
              <div className="flex gap-3 sm:gap-4 sm:flex-row flex-col">
                <div className="mb-3 sm:mb-5 flex-1">
                  <label className="block text-sm font-medium mb-1.5 sm:mb-2 text-slate-900">Nama Bisnis *</label>
                  <input type="text" name="business_name" value={formData.business_name} onChange={handleChange} placeholder="Contoh: Chatshop Official" className={`w-full px-4 py-2.5 sm:py-3 font-inherit text-[0.95rem] border rounded-md text-slate-900 bg-white transition-all duration-200 focus:outline-none ${errors.business_name ? 'border-red-500 bg-red-50 focus:ring-4 focus:ring-red-500/20' : 'border-slate-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/20'}`} />
                  {errors.business_name && <span className="block text-[0.75rem] text-red-500 mt-1.5 font-medium">{errors.business_name}</span>}
                </div>
                <div className="mb-3 sm:mb-5 flex-1">
                  <label className="block text-sm font-medium mb-1.5 sm:mb-2 text-slate-900">Nama Pemilik *</label>
                  <input type="text" name="owner_name" value={formData.owner_name} onChange={handleChange} placeholder="Nama lengkap pemilik" className={`w-full px-4 py-2.5 sm:py-3 font-inherit text-[0.95rem] border rounded-md text-slate-900 bg-white transition-all duration-200 focus:outline-none ${errors.owner_name ? 'border-red-500 bg-red-50 focus:ring-4 focus:ring-red-500/20' : 'border-slate-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/20'}`} />
                  {errors.owner_name && <span className="block text-[0.75rem] text-red-500 mt-1.5 font-medium">{errors.owner_name}</span>}
                </div>
              </div>
              <div className="flex gap-3 sm:gap-4 sm:flex-row flex-col">
                <div className="mb-3 sm:mb-5 flex-1">
                  <label className="block text-sm font-medium mb-1.5 sm:mb-2 text-slate-900">Username *</label>
                  <input type="text" name="username" value={formData.username} onChange={handleChange} placeholder="Username unik (tanpa spasi)" className={`w-full px-4 py-2.5 sm:py-3 font-inherit text-[0.95rem] border rounded-md text-slate-900 bg-white transition-all duration-200 focus:outline-none ${errors.username ? 'border-red-500 bg-red-50 focus:ring-4 focus:ring-red-500/20' : 'border-slate-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/20'}`} />
                  {errors.username && <span className="block text-[0.75rem] text-red-500 mt-1.5 font-medium">{errors.username}</span>}
                </div>
                <div className="mb-3 sm:mb-5 flex-1">
                  <label className="block text-sm font-medium mb-1.5 sm:mb-2 text-slate-900">Telepon Bisnis *</label>
                  <div className="flex relative">
                    <span className={`inline-flex items-center px-3 text-[0.95rem] font-medium text-slate-600 bg-slate-50 border border-r-0 rounded-l-md ${errors.phone_number ? 'border-red-500' : 'border-slate-200'}`}>
                      +62
                    </span>
                    <input type="tel" name="phone_number" value={formData.phone_number} onChange={handleChange} placeholder="8123456789" className={`w-full px-4 py-2.5 sm:py-3 font-inherit text-[0.95rem] border rounded-r-md text-slate-900 bg-white transition-all duration-200 focus:outline-none ${errors.phone_number ? 'border-red-500 bg-red-50 focus:ring-4 focus:ring-red-500/20' : 'border-slate-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/20'}`} />
                  </div>
                  {errors.phone_number && <span className="block text-[0.75rem] text-red-500 mt-1.5 font-medium">{errors.phone_number}</span>}
                </div>
              </div>

              <div className="mb-3 sm:mb-5">
                <label className="block text-sm font-medium mb-1.5 sm:mb-2 text-slate-900">Email *</label>
                <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="email@bisnis.com" className={`w-full px-4 py-2.5 sm:py-3 font-inherit text-[0.95rem] border rounded-md text-slate-900 bg-white transition-all duration-200 focus:outline-none ${errors.email ? 'border-red-500 bg-red-50 focus:ring-4 focus:ring-red-500/20' : 'border-slate-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/20'}`} />
                {errors.email && <span className="block text-[0.75rem] text-red-500 mt-1.5 font-medium">{errors.email}</span>}
              </div>

              <div className="flex gap-3 sm:gap-4 sm:flex-row flex-col">
                <div className="mb-3 sm:mb-5 flex-1">
                  <label className="block text-sm font-medium mb-1.5 sm:mb-2 text-slate-900">Nomor WhatsApp Baru AI (Opsional)</label>
                  <div className="flex relative">
                    <span className={`inline-flex items-center px-3 text-[0.95rem] font-medium text-slate-600 bg-slate-50 border border-r-0 rounded-l-md ${errors.whatsapp_number ? 'border-red-500' : 'border-slate-200'}`}>
                      +62
                    </span>
                    <input type="tel" name="whatsapp_number" value={formData.whatsapp_number} onChange={handleChange} placeholder="8123456789" className={`w-full px-4 py-2.5 sm:py-3 font-inherit text-[0.95rem] border rounded-r-md text-slate-900 bg-white transition-all duration-200 focus:outline-none ${errors.whatsapp_number ? 'border-red-500 bg-red-50 focus:ring-4 focus:ring-red-500/20' : 'border-slate-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/20'}`} />
                  </div>
                  {errors.whatsapp_number && <span className="block text-[0.75rem] text-red-500 mt-1.5 font-medium">{errors.whatsapp_number}</span>}
                </div>
                <div className="mb-3 sm:mb-5 flex-1 flex flex-col">
                  <label className="hidden sm:block text-sm font-medium mb-1.5 sm:mb-2 text-slate-900 opacity-0 select-none">Catatan</label>
                  <div className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-md text-[0.8rem] text-slate-600 leading-relaxed flex items-center shadow-sm h-full sm:h-auto">
                    <span><strong>Info:</strong> Nomor ini khusus untuk AI. Pastikan belum pernah terdaftar di WA Messenger.</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 sm:gap-4 sm:flex-row flex-col">
                <div className="mb-3 sm:mb-5 flex-1">
                  <label className="block text-sm font-medium mb-1.5 sm:mb-2 text-slate-900">Kata Sandi *</label>
                  <div className="relative">
                    <input type={showPassword ? "text" : "password"} name="password" value={formData.password} onChange={handleChange} placeholder="Minimal 8 karakter" className={`w-full px-4 py-2.5 sm:py-3 pr-12 font-inherit text-[0.95rem] border rounded-md text-slate-900 bg-white transition-all duration-200 focus:outline-none ${errors.password ? 'border-red-500 bg-red-50 focus:ring-4 focus:ring-red-500/20' : 'border-slate-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/20'}`} />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-4 flex items-center text-slate-400 hover:text-emerald-500">
                      <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} className="h-5 w-5" />
                    </button>
                  </div>
                  {/* Password Strength Indicator Visual Bars */}
                  {formData.password && (
                    <div className="mt-2">
                      <div className="flex gap-1 mb-1">
                        {[1, 2, 3, 4].map((val: any) => (
                          <div
                            key={val}
                            className="flex-1 h-1 rounded-sm transition-all duration-300"
                            style={{
                              backgroundColor: val <= passwordStrength.score ? passwordStrength.color : '#e2e8f0'
                            }}
                          ></div>
                        ))}
                      </div>
                      <div className="text-[0.7rem] font-semibold text-left" style={{ color: passwordStrength.color }}>
                        {passwordStrength.label}
                      </div>
                    </div>
                  )}
                  {errors.password && <span className="block text-[0.75rem] text-red-500 mt-1.5 font-medium">{errors.password}</span>}
                </div>
                <div className="mb-3 sm:mb-5 flex-1">
                  <label className="block text-sm font-medium mb-1.5 sm:mb-2 text-slate-900">Konfirmasi Sandi *</label>
                  <div className="relative">
                    <input type={showConfirmPassword ? "text" : "password"} name="confirm_password" value={formData.confirm_password} onChange={handleChange} placeholder="Ulangi kata sandi" className={`w-full px-4 py-2.5 sm:py-3 pr-12 font-inherit text-[0.95rem] border rounded-md text-slate-900 bg-white transition-all duration-200 focus:outline-none ${errors.confirm_password ? 'border-red-500 bg-red-50 focus:ring-4 focus:ring-red-500/20' : 'border-slate-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/20'}`} />
                    <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute inset-y-0 right-4 flex items-center text-slate-400 hover:text-emerald-500">
                      <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} className="h-5 w-5" />
                    </button>
                  </div>
                  {errors.confirm_password && <span className="block text-[0.75rem] text-red-500 mt-1.5 font-medium">{errors.confirm_password}</span>}
                </div>
              </div>
              <label className="flex items-start gap-3 mt-4 cursor-pointer">
                <input type="checkbox" name="agree_terms" checked={formData.agree_terms} onChange={handleChange} className="w-[18px] h-[18px] mt-1 accent-emerald-500 cursor-pointer flex-shrink-0" />
                <span className="text-[0.85rem] leading-relaxed text-slate-900 select-none">
                  Saya telah membaca dan menyetujui {' '}
                  <a href="https://chatshop.pagii.co/terms" target="_blank" rel="noreferrer" className="text-emerald-500 underline cursor-pointer text-[0.85rem] bg-none border-none p-0">Ketentuan Layanan</a>
                  {' '} dan {' '}
                  <a href="https://chatshop.pagii.co/privacy" target="_blank" rel="noreferrer" className="text-emerald-500 underline cursor-pointer text-[0.85rem] bg-none border-none p-0">Kebijakan Privasi</a>
                  {' '} dari Chatshop.
                </span>
              </label>
              {errors.agree_terms && <p className="block text-[0.75rem] text-red-500 mt-1.5 font-medium">{errors.agree_terms}</p>}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="flex gap-4 mb-6 sm:flex-row flex-col">
                <label
                  className={`flex-1 border rounded-lg p-5 cursor-pointer transition-all duration-200 flex items-start gap-4 hover:border-slate-500 ${metaOption === 'auto' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200'} opacity-50 cursor-not-allowed pointer-events-none relative`}
                >
                  <input
                    type="radio"
                    name="metaOption"
                    value="auto"
                    checked={metaOption === 'auto'}
                    onChange={() => setMetaOption('auto')}
                    disabled
                    className="w-auto mt-[0.2rem] accent-emerald-500 cursor-pointer"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="m-0 mb-1 text-base font-semibold">Koneksi Cepat (Otomatis)</h3>
                      <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#FFF3CD] text-[#856404] border border-[#FFEEBA] whitespace-nowrap">
                        Coming Soon
                      </span>
                    </div>
                    <p className="m-0 text-[0.85rem] text-slate-500 leading-[1.4]">Hubungkan WhatsApp Anda langsung via Facebook.</p>
                  </div>
                </label>
                <label className={`flex-1 border rounded-lg p-5 cursor-pointer transition-all duration-200 flex items-start gap-4 hover:border-slate-500 ${metaOption === 'team' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200'}`}>
                  <input type="radio" name="metaOption" value="team" checked={metaOption === 'team'} onChange={() => setMetaOption('team')} className="w-auto mt-[0.2rem] accent-emerald-500 cursor-pointer" />
                  <div className="flex-1">
                    <h3 className="m-0 mb-1 text-base font-semibold">Dibantu Team</h3>
                    <p className="m-0 text-[0.85rem] text-slate-500 leading-[1.4]">Tim Chatshop akan melakukan proses konfigurasi WhatsApp Business ke Meta. Anda akan mendapat notifikasi saat sudah siap digunakan.</p>
                  </div>
                </label>
              </div>

              {metaOption === 'auto' && (
                <div className="text-center p-8 border border-dashed border-slate-300 rounded-lg">
                  <button type="button" className="px-6 py-3 font-semibold text-[0.95rem] rounded-md transition-all duration-200 bg-emerald-500 text-white hover:bg-emerald-600 border-none cursor-pointer" onClick={() => (window as any).FB.login(() => { }, { config_id: '' /* import.meta.env.VITE_FB_CONFIG_ID */, response_type: 'code', override_default_response_type: true })}>
                    Hubungkan Facebook & WhatsApp
                  </button>
                  {formData.whatsapp_phone_number_id && <p className="text-emerald-500 font-medium mt-2">✓ Terhubung</p>}
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6 relative min-h-[350px]">
              {isPaymentLoading && formData.payment_type === 'PLATFORM_FEE' && (
                <div className="flex flex-col items-center justify-center text-center absolute inset-0 z-50 bg-white rounded-xl">
                  <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CircularProgress color="success" size={40} thickness={4} />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2 sm:mb-3">Memproses Pendaftaran...</h3>
                  <p className="text-slate-500 text-[0.95rem] sm:text-[1.05rem] max-w-sm sm:max-w-md mx-auto leading-relaxed">
                    Mohon tunggu sebentar, kami sedang menyiapkan dashboard Anda.
                  </p>
                </div>
              )}

              {showMonthlyPayment && formData.payment_type === 'MONTHLY_FEE' ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between pb-3 mb-2 border-b border-slate-100">
                    <div>
                      <h3 className="text-lg font-bold text-slate-900 m-0">Pembayaran ChatShop Monthly</h3>
                      <p className="text-xs text-slate-500 m-0 mt-0.5">Selesaikan pembayaran langganan bulanan Anda (Rp{formattedMonthlyFee}/bln)</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowMonthlyPayment(false)}
                      className="text-xs text-emerald-600 hover:text-emerald-700 font-semibold bg-emerald-50 hover:bg-emerald-100 px-3 py-1.5 rounded-lg border-none cursor-pointer transition-colors"
                    >
                      ← Ubah Paket
                    </button>
                  </div>

                  {isPaymentSuccess ? (
                    <div className="flex flex-col items-center justify-center text-center py-10 px-2 sm:py-16 sm:px-8">
                      {isPaymentLoading ? (
                        <>
                          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <CircularProgress color="success" size={40} thickness={4} />
                          </div>
                          <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2 sm:mb-3">Memproses Pendaftaran...</h3>
                          <p className="text-slate-500 text-[0.95rem] sm:text-[1.05rem] max-w-sm sm:max-w-md mx-auto leading-relaxed">
                            Pembayaran Anda telah berhasil. Mohon tunggu sebentar, kami sedang menyiapkan dashboard Anda.
                          </p>
                        </>
                      ) : (
                        <>
                          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-red-100 rounded-full flex items-center justify-center mb-5 sm:mb-6">
                            <svg className="w-8 h-8 sm:w-10 sm:h-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                          </div>
                          <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2 sm:mb-3">Pendaftaran Tertunda</h3>
                          <p className="text-slate-500 text-[0.95rem] sm:text-[1.05rem] max-w-sm sm:max-w-md mx-auto leading-relaxed mb-6 sm:mb-8">
                            Pembayaran berhasil, namun terjadi kesalahan sistem saat mendaftarkan akun. Silakan coba lagi.
                          </p>
                          <button
                            onClick={handleRegisterMerchant}
                            className="px-6 py-3 sm:px-8 sm:py-3.5 font-semibold text-[0.95rem] sm:text-[1rem] rounded-lg transition-all duration-200 bg-emerald-500 text-white hover:bg-emerald-600 shadow-lg shadow-emerald-500/30 border-none cursor-pointer w-full sm:w-auto"
                          >
                            Coba Lagi Pendaftaran
                          </button>
                        </>
                      )}
                    </div>
                  ) : isSignatureLoading ? (
                    <div className="flex flex-col items-center justify-center text-center py-16 px-4">
                      <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-5">
                        <CircularProgress color="success" size={36} thickness={4} />
                      </div>
                      <h3 className="text-xl font-bold text-slate-900 mb-2">Menyiapkan Formulir Pembayaran</h3>
                      <p className="text-slate-500 text-[0.95rem] max-w-sm mx-auto leading-relaxed">
                        Sedang membuat sesi pembayaran yang aman dengan server billing, mohon tunggu sebentar...
                      </p>
                    </div>
                  ) : signatureError ? (
                    <div className="flex flex-col items-center justify-center text-center py-12 px-4">
                      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-5">
                        <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      </div>
                      <h3 className="text-xl font-bold text-slate-900 mb-2">Gagal Menyiapkan Pembayaran</h3>
                      <p className="text-slate-500 text-[0.95rem] max-w-md mx-auto mb-6 leading-relaxed">
                        {signatureError}
                      </p>
                      <div className="flex flex-col sm:flex-row items-center gap-3 justify-center">
                        <button
                          type="button"
                          onClick={fetchBillingSignature}
                          className="px-6 py-2.5 font-semibold text-[0.95rem] rounded-lg transition-all duration-200 bg-emerald-500 text-white hover:bg-emerald-600 shadow-md shadow-emerald-500/20 border-none cursor-pointer w-full sm:w-auto"
                        >
                          Coba Lagi
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowMonthlyPayment(false);
                            setSignatureError('');
                          }}
                          className="px-5 py-2.5 font-semibold text-[0.95rem] rounded-lg transition-all duration-200 bg-slate-100 text-slate-600 hover:bg-slate-200 border-none cursor-pointer w-full sm:w-auto"
                        >
                          Kembali ke Pilihan Paket
                        </button>
                      </div>
                    </div>
                  ) : (
                    (() => {
                      const baseWidgetUrl = paymentUrl || process.env.REACT_APP_BILLING_WIDGET_URL || "";
                      const separator = baseWidgetUrl.includes('?') ? '&' : '?';
                      const finalUrl = `${baseWidgetUrl}${separator}sig=${encodeURIComponent(signatureToken)}`;

                      return (
                        <div className="space-y-4">
                          <style>{`
                            .billing-iframe {
                              display: block;
                              width: 100%;
                              min-height: 600px;
                              height: 80vh;
                              max-height: 900px;
                              border: 1px solid #e2e8f0;
                              border-radius: 8px;
                              touch-action: auto;
                              pointer-events: auto;
                              -webkit-overflow-scrolling: touch;
                            }
                            @media (max-width: 640px) {
                              .billing-iframe {
                                min-height: 500px;
                                height: 75vh;
                                max-height: 700px;
                              }
                            }
                          `}</style>
                          <iframe
                            key={finalUrl}
                            src={finalUrl}
                            title="Billing"
                            className="billing-iframe"
                            frameBorder="0"
                            scrolling="yes"
                            allow="payment *"
                            sandbox="allow-forms allow-scripts allow-same-origin allow-popups allow-modals allow-orientation-lock allow-pointer-lock allow-popups-to-escape-sandbox allow-presentation allow-storage-access-by-user-activation allow-top-navigation allow-top-navigation-by-user-activation"
                          ></iframe>
                        </div>
                      );
                    })()
                  )}
                </div>
              ) : (
                <div className={`transition-opacity duration-300 ${isPaymentLoading && formData.payment_type === 'PLATFORM_FEE' ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
                  <div className="text-center mb-6">
                    <h2 className="text-xl font-bold text-slate-900">Pilih Paket Berlangganan</h2>
                    <p className="text-slate-500 text-[0.95rem] mt-1">Pilih paket yang paling sesuai dengan kebutuhan bisnis Anda.</p>
                  </div>
                  <div className="flex gap-4 flex-col sm:flex-row">
                    <label className={`flex-1 border-2 rounded-xl p-5 sm:p-6 cursor-pointer transition-all duration-300 relative group overflow-hidden flex flex-col items-center justify-center ${formData.payment_type === 'MONTHLY_FEE' ? 'border-emerald-500 bg-emerald-50/50 shadow-[0_4px_20px_-4px_rgba(16,185,129,0.2)]' : 'border-slate-200 hover:border-emerald-300 hover:bg-slate-50 hover:shadow-[0_4px_20px_-4px_rgba(16,185,129,0.1)]'}`}>
                      <input type="radio" name="payment_type" value="MONTHLY_FEE" checked={formData.payment_type === 'MONTHLY_FEE'} onChange={handleChange} className="absolute w-0 h-0 opacity-0" />

                      <div className={`absolute top-4 right-4 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${formData.payment_type === 'MONTHLY_FEE' ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300 bg-white'}`}>
                        {formData.payment_type === 'MONTHLY_FEE' && <div className="w-2 h-2 rounded-full bg-white" />}
                      </div>

                      <div className="w-full text-center relative z-10 flex flex-col items-center mt-2">
                        <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 transition-colors ${formData.payment_type === 'MONTHLY_FEE' ? 'bg-emerald-100 text-emerald-500' : 'bg-slate-100 text-slate-400 group-hover:text-emerald-400'}`}>
                          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                        </div>
                        <h3 className={`text-lg sm:text-xl font-bold mb-2 transition-colors ${formData.payment_type === 'MONTHLY_FEE' ? 'text-emerald-900' : 'text-slate-900'}`}>ChatShop Monthly</h3>
                        <div className={`font-bold text-2xl sm:text-3xl mb-5 transition-colors ${formData.payment_type === 'MONTHLY_FEE' ? 'text-emerald-600' : 'text-slate-700'}`}>
                          Rp{formattedMonthlyFee} <span className={`text-sm sm:text-base font-normal ${formData.payment_type === 'MONTHLY_FEE' ? 'text-emerald-500/80' : 'text-slate-400'}`}>/ bulan</span>
                        </div>
                        <ul className="text-left space-y-3 w-full max-w-[240px] mx-auto">
                          {['Semua fitur ChatShop', 'AI WhatsApp', 'Dashboard Merchant', 'Support Prioritas'].map((feat, idx) => (
                            <li key={idx} className={`flex items-start text-[0.9rem] sm:text-[0.95rem] transition-colors leading-tight ${formData.payment_type === 'MONTHLY_FEE' ? 'text-emerald-800' : 'text-slate-600'}`}>
                              <svg className={`w-4 h-4 mr-3 mt-[0.15rem] flex-shrink-0 ${formData.payment_type === 'MONTHLY_FEE' ? 'text-emerald-500' : 'text-slate-300 group-hover:text-emerald-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                              <span>{feat}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </label>

                    <label className={`flex-1 border-2 rounded-xl p-5 sm:p-6 cursor-pointer transition-all duration-300 relative group overflow-hidden flex flex-col items-center justify-center ${formData.payment_type === 'PLATFORM_FEE' ? 'border-emerald-500 bg-emerald-50/50 shadow-[0_4px_20px_-4px_rgba(16,185,129,0.2)]' : 'border-slate-200 hover:border-emerald-300 hover:bg-slate-50 hover:shadow-[0_4px_20px_-4px_rgba(16,185,129,0.1)]'}`}>
                      <input type="radio" name="payment_type" value="PLATFORM_FEE" checked={formData.payment_type === 'PLATFORM_FEE'} onChange={handleChange} className="absolute w-0 h-0 opacity-0" />

                      <div className={`absolute top-4 right-4 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${formData.payment_type === 'PLATFORM_FEE' ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300 bg-white'}`}>
                        {formData.payment_type === 'PLATFORM_FEE' && <div className="w-2 h-2 rounded-full bg-white" />}
                      </div>

                      <div className="w-full text-center relative z-10 flex flex-col items-center mt-2">
                        <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 transition-colors ${formData.payment_type === 'PLATFORM_FEE' ? 'bg-emerald-100 text-emerald-500' : 'bg-slate-100 text-slate-400 group-hover:text-emerald-400'}`}>
                          <span className="text-2xl font-bold">%</span>
                        </div>
                        <h3 className={`text-lg sm:text-xl font-bold mb-2 transition-colors ${formData.payment_type === 'PLATFORM_FEE' ? 'text-emerald-900' : 'text-slate-900'}`}>Pay as You Sell</h3>
                        <div className={`font-bold text-2xl sm:text-3xl mb-5 transition-colors ${formData.payment_type === 'PLATFORM_FEE' ? 'text-emerald-600' : 'text-slate-700'}`}>
                          {platformFeePercentage}% <span className={`text-sm sm:text-base font-normal ${formData.payment_type === 'PLATFORM_FEE' ? 'text-emerald-500/80' : 'text-slate-400'}`}>/ transaksi</span>
                        </div>
                        <ul className="text-left space-y-3 w-full max-w-[240px] mx-auto">
                          {['Semua fitur ChatShop', 'Tidak ada biaya bulanan', 'Bayar hanya jika sukses', 'Support Prioritas'].map((feat, idx) => (
                            <li key={idx} className={`flex items-start text-[0.9rem] sm:text-[0.95rem] transition-colors leading-tight ${formData.payment_type === 'PLATFORM_FEE' ? 'text-emerald-800' : 'text-slate-600'}`}>
                              <svg className={`w-4 h-4 mr-3 mt-[0.15rem] flex-shrink-0 ${formData.payment_type === 'PLATFORM_FEE' ? 'text-emerald-500' : 'text-slate-300 group-hover:text-emerald-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                              <span>{feat}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </label>
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold mb-4 text-slate-900">Pendaftaran Berhasil!</h2>
              <p className="text-lg text-slate-600 mb-6">Kami telah mengirimkan link verifikasi email, silakan periksa kotak masuk Anda.</p>

              {hadWhatsappNumber ? (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8 max-w-lg mx-auto text-left shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="mt-1 flex-shrink-0">
                      <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-[1.05rem] font-semibold text-blue-900 mb-2">Penting</h4>
                      <p className="text-blue-800 text-[0.95rem] leading-relaxed m-0">
                        Tim kami akan segera menghubungi Anda dalam waktu <strong>2x24 jam</strong> untuk membantu proses aktivasi <strong>WhatsApp AI</strong> Anda.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8 max-w-lg mx-auto text-left shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="mt-1 flex-shrink-0">
                      <svg className="w-6 h-6 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-[1.05rem] font-semibold text-amber-900 mb-2">Informasi</h4>
                      <p className="text-amber-800 text-[0.95rem] leading-relaxed m-0">
                        Anda dapat mengisi <strong>Nomor WhatsApp AI</strong> setelah login ke CMS untuk mulai menggunakan layanan WhatsApp AI dan mengaktifkan seluruh fitur yang tersedia.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <button className="px-8 py-3.5 font-semibold text-[1rem] rounded-lg transition-all duration-200 bg-emerald-500 text-white hover:bg-emerald-600 shadow-lg shadow-emerald-500/30 border-none cursor-pointer" onClick={() => {
                localStorage.clear();
                sessionStorage.clear();
                navigate('/login');
              }}>Masuk Dashboard</button>
            </div>
          )}
        </div>

        {step < 4 && !isPaymentLoading && !showMonthlyPayment && (
          <div className="px-6 sm:px-12 py-6 border-t border-slate-200 bg-slate-50">
            <div className="flex justify-between items-center w-full">
              <button
                className="px-6 py-3 font-semibold text-[0.95rem] rounded-md transition-all duration-200 bg-white text-slate-500 border border-slate-200 hover:bg-slate-50 hover:text-slate-900 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => {
                  if (step === 1) navigate('/login');
                  else { setStep(step - 1); setErrors({}); }
                }}
                disabled={isSubmitting}
              >
                Kembali
              </button>
              <button
                className="px-6 py-3 font-semibold text-[0.95rem] rounded-md transition-all duration-200 bg-emerald-500 text-white hover:bg-emerald-600 border-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleNext}
                disabled={isSubmitting}
              >
                {isSubmitting || isPaymentLoading
                  ? 'Memproses...'
                  : step === 3
                    ? (formData.payment_type === 'MONTHLY_FEE' ? 'Lanjutkan Pembayaran' : 'Daftar Sekarang')
                    : 'Selanjutnya'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


const RegisterWithReCaptcha = () => (
  <GoogleReCaptchaProvider reCaptchaKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ""}>
    <Register />
  </GoogleReCaptchaProvider>
);

export default RegisterWithReCaptcha;